import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * Диалог подтверждения снятия сайта с публикации.
 * onConfirm(siteId) выполняет снятие и бросает ошибку при неудаче.
 */
const UnpublishDialog = ({ site, siteUrl, onConfirm, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isProcessing) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isProcessing, onClose]);

  const handleConfirm = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setError(null);
    try {
      await onConfirm(site?.id);
      onClose();
    } catch (err) {
      console.error('Error unpublishing site:', err);
      setError(err?.message || 'Не удалось снять сайт с публикации');
      setIsProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4"
      onClick={() => !isProcessing && onClose()}
    >
      <div
        className="bg-card rounded-2xl shadow-elevation-4 border border-border p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unpublish-dialog-title"
      >
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
            <Icon name="EyeOff" size={24} className="text-warning" />
          </div>
          <div>
            <h3 id="unpublish-dialog-title" className="text-xl font-semibold text-foreground mb-2">
              Снять сайт с публикации?
            </h3>
            <p className="text-text-secondary">
              «{site?.name}» перестанет открываться{siteUrl ? ` по адресу ${siteUrl}` : ''}.
              Контент и настройки сохранятся — сайт можно опубликовать снова.
            </p>
            {error && <p className="text-error text-sm mt-2">{error}</p>}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing}
            className="flex-1 px-4 py-2.5 bg-warning text-warning-foreground rounded-xl text-sm font-medium hover:bg-warning/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isProcessing && (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {isProcessing ? 'Снимаем...' : 'Снять с публикации'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnpublishDialog;
